import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import { useCommitments } from "@/hooks/use-commitments";
import { useProjectContractors } from "@/hooks/use-project-contractors";
import { apiFetch, apiPatch, apiPost } from "@/lib/api";
import { punchListKeys } from "@/lib/query";

export type PunchItemStatus =
  | "open"
  | "in_progress"
  | "ready_for_review"
  | "verified"
  | "rejected"
  | "closed"
  | "void";

export type PunchItemPriority = "low" | "medium" | "high" | "critical";

export interface PunchItem {
  id: string;
  orgId: string;
  projectId: string;
  /** Joined from `projects.name` for display */
  projectName: string | null;
  punchNumber: string;
  location: string | null;
  roomOrArea: string | null;
  description: string;
  status: PunchItemStatus;
  priority: PunchItemPriority;
  trade: string | null;
  assignedUserId: string | null;
  assignedTo: string | null;
  assignedCompanyId: string | null;
  assignedCompanyName: string | null;
  reportedByUserId: string;
  creatorId: string;
  verifiedByUserId: string | null;
  verifiedAt: string | null;
  verificationNote: string | null;
  costImpact: number | null;
  dueDate: string | null;
  createdDate: string | null;
  completedDate: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PunchListFilters {
  projectId?: string;
  status?: string;
  priority?: string;
  trade?: string;
  location?: string;
  overdue?: boolean;
}

export interface PunchBulkResult {
  succeeded: string[];
  failed: { id: string; error: string }[];
}

function buildPunchListUrl(filters?: PunchListFilters): string {
  const params = new URLSearchParams();
  if (filters?.projectId) params.set("projectId", filters.projectId);
  if (filters?.status) params.set("status", filters.status);
  if (filters?.priority) params.set("priority", filters.priority);
  if (filters?.trade) params.set("trade", filters.trade);
  if (filters?.location) params.set("location", filters.location);
  if (filters?.overdue) params.set("overdue", "true");
  const qs = params.toString();
  return qs ? `/punchlist?${qs}` : "/punchlist";
}

function bulkResultToast(result: PunchBulkResult, successLabel: string): void {
  const { succeeded, failed } = result;
  if (succeeded.length > 0 && failed.length === 0) {
    toast.success(`${successLabel}: ${succeeded.length} item${succeeded.length === 1 ? "" : "s"}`);
  } else if (succeeded.length > 0 && failed.length > 0) {
    toast.warning(`${successLabel}: ${succeeded.length} succeeded, ${failed.length} failed`);
  } else {
    toast.error(`Bulk action failed for ${failed.length} item${failed.length === 1 ? "" : "s"}`);
  }
}

export function usePunchList(filters?: PunchListFilters) {
  return useQuery({
    queryKey: [...punchListKeys.all, filters ?? null],
    queryFn: () => apiFetch<PunchItem[]>(buildPunchListUrl(filters)),
  });
}

export function usePunchItem(id: string, enabled = true) {
  return useQuery({
    queryKey: [...punchListKeys.all, id],
    queryFn: () => apiFetch<PunchItem>(`/punchlist/${id}`),
    enabled,
  });
}

export interface CreatePunchItemInput {
  projectId: string;
  description: string;
  location?: string;
  roomOrArea?: string;
  priority: PunchItemPriority;
  dueDate?: string;
  assignedCompanyId?: string;
  trade?: string;
}

export function useCreatePunchItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePunchItemInput) => apiPost<PunchItem>("/punchlist", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: punchListKeys.all });
      toast.success("Punch item created");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to create punch item");
    },
  });
}

export interface UpdatePunchItemInput {
  description?: string;
  location?: string | null;
  roomOrArea?: string | null;
  priority?: PunchItemPriority;
  status?: PunchItemStatus;
  dueDate?: string | null;
  assignedCompanyId?: string | null;
  trade?: string | null;
  assignedTo?: string | null;
}

export function useUpdatePunchItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdatePunchItemInput & { id: string }) =>
      apiPatch<PunchItem>(`/punchlist/${id}`, input),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: punchListKeys.all });
      void qc.invalidateQueries({ queryKey: [...punchListKeys.all, vars.id] });
      toast.success("Punch item updated");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update punch item");
    },
  });
}

export interface VerifyPunchItemInput {
  id: string;
  action: "accept" | "reject" | "reassign";
  note?: string;
  reassignCompanyId?: string;
}

export function useVerifyPunchItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: VerifyPunchItemInput) =>
      apiPost<PunchItem>(`/punchlist/${id}/verify`, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: punchListKeys.all });
      void qc.invalidateQueries({ queryKey: [...punchListKeys.all, vars.id] });
      const label =
        vars.action === "accept"
          ? "Punch item accepted and closed"
          : vars.action === "reject"
            ? "Punch item rejected"
            : "Punch item re-assigned";
      toast.success(label);
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to verify punch item");
    },
  });
}

/** Companies allowed as punch assignees (project team + commitment holders); matches API manpower gate. */
export function usePunchAssignableCompanies(projectId: string) {
  const enabled = Boolean(projectId);
  const { data: contractors = [], isLoading: teamLoading } = useProjectContractors(projectId);
  const { data: commitments = [], isLoading: commitmentsLoading } = useCommitments(
    enabled ? { projectId } : undefined,
    { enabled },
  );

  const options = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of contractors) {
      const label = c.companyName?.trim() ? c.companyName : "Unnamed company";
      byId.set(c.contractorId, label);
    }
    for (const cm of commitments) {
      const id = cm.companyId;
      if (!id) continue;
      if (!byId.has(id)) {
        byId.set(id, cm.companyName?.trim() ? cm.companyName : "Unnamed company");
      }
    }
    return [...byId.entries()]
      .map(([cid, name]) => ({ id: cid, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [contractors, commitments]);

  return { options, isLoading: teamLoading || commitmentsLoading };
}

export function useBulkAssignPunchItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; assignedCompanyId: string }) =>
      apiPost<PunchBulkResult>("/punchlist/bulk-assign", input),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: punchListKeys.all });
      bulkResultToast(result, "Assigned");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Bulk assign failed");
    },
  });
}

export function useBulkClosePunchItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { ids: string[]; note?: string }) =>
      apiPost<PunchBulkResult>("/punchlist/bulk-close", input),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: punchListKeys.all });
      bulkResultToast(result, "Closed");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Bulk close failed");
    },
  });
}
